import { create } from 'zustand';

/**
 * Unsaved Changes Guard Store
 *
 * Promise-based gate that asks the user what to do with unsaved edits before
 * a destructive document flow (Open / New / restore) replaces the working
 * document. Mirrors BIM's "Before you cross out a bunch of work…" prompt.
 *
 * Usage: `const proceed = await useUnsavedChangesStore.getState().request();`
 * The dialog component mounted once in ProjectDialogsHost settles the
 * promise through `close()`.
 */

interface UnsavedChangesState {
    isOpen: boolean;
    /** Pending resolver; exactly one at a time. */
    resolver: ((proceed: boolean) => void) | null;
    /** Opens the prompt (no-op when one is already visible). */
    request: () => Promise<boolean>;
    /** Settles the pending request and hides the dialog. */
    close: (proceed: boolean) => void;
}

export const useUnsavedChangesStore = create<UnsavedChangesState>()((set, get) => ({
    isOpen: false,
    resolver: null,

    request: () => {
        if (get().isOpen) return Promise.resolve(false);
        return new Promise<boolean>((resolve) => {
            set({ isOpen: true, resolver: resolve });
        });
    },

    close: (proceed) => {
        const resolver = get().resolver;
        set({ isOpen: false, resolver: null });
        resolver?.(proceed);
    },
}));
